import { MediaAssetItem } from '@/types';
import { generateId, audioBufferToWav } from './helpers';

// Generate synthesized audio WAV for stock background music presets offline
function createSynthesizedAudioDataUrl(type: 'lofi' | 'upbeat' | 'chime'): string {
  try {
    const sampleRate = 22050;
    const duration = type === 'chime' ? 2.5 : type === 'upbeat' ? 12 : 16;
    const numSamples = Math.floor(sampleRate * duration);

    // Audio buffer creation in memory (using OfflineAudioContext or fallback wav synthesis)
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (AudioCtx) {
      const ctx = new AudioCtx({ sampleRate });
      const buffer = ctx.createBuffer(1, numSamples, sampleRate);
      const data = buffer.getChannelData(0);

      const bpm = type === 'upbeat' ? 120 : 75;
      const beatLen = 60 / bpm;

      for (let i = 0; i < numSamples; i++) {
        const t = i / sampleRate;
        let sample = 0;

        if (type === 'chime') {
          // Bell chime harmonics decaying smoothly
          const env = Math.exp(-t * 2.2);
          sample = (
            Math.sin(2 * Math.PI * 880 * t) * 0.4 +
            Math.sin(2 * Math.PI * 1320 * t) * 0.25 +
            Math.sin(2 * Math.PI * 1760 * t) * 0.15
          ) * env;
        } else if (type === 'lofi') {
          // Lofi warm chord progression (Fmaj7 -> Em7 -> Dm7 -> Cmaj7)
          const bar = (t / (beatLen * 2)) % 4;
          const chordFreqs =
            bar < 1 ? [349.23, 440.0, 523.25, 659.25] // Fmaj7
            : bar < 2 ? [329.63, 392.0, 493.88, 587.33] // Em7
            : bar < 3 ? [293.66, 349.23, 440.0, 523.25] // Dm7
            : [261.63, 329.63, 392.0, 493.88]; // Cmaj7

          const chordSound = chordFreqs.reduce((acc, freq) => {
            return acc + Math.sin(2 * Math.PI * freq * t) * 0.08;
          }, 0);

          // Soft vinyl tape hiss + gentle pulse
          const pulse = (1 + Math.sin(2 * Math.PI * (1 / beatLen) * t)) * 0.5;
          const softNoise = (Math.random() * 2 - 1) * 0.015;
          sample = chordSound * 0.5 * (0.8 + 0.2 * pulse) + softNoise;
        } else {
          // Upbeat rhythmic acoustic groove
          const beatIndex = Math.floor(t / beatLen);
          const beatPhase = (t % beatLen) / beatLen;
          const kickEnv = Math.exp(-beatPhase * 14);
          const kick = (beatIndex % 2 === 0) ? Math.sin(2 * Math.PI * 65 * beatPhase) * kickEnv * 0.4 : 0;
          const hat = Math.exp(-beatPhase * 25) * (Math.random() * 2 - 1) * 0.15;
          const bassNote = [130.81, 146.83, 164.81, 196.0][beatIndex % 4];
          const bass = Math.sin(2 * Math.PI * bassNote * t) * 0.2;
          sample = kick + hat + bass;
        }

        data[i] = Math.max(-1, Math.min(1, sample));
      }

      const wavBlob = audioBufferToWav(buffer);
      return URL.createObjectURL(wavBlob);
    }
  } catch (e) {
    console.warn('Synthesized audio fallback error:', e);
  }

  // Fallback empty small WAV data URL
  return 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';
}

// SVG Stock Watermarks & Badges
const SVG_SUBSCRIBE_BADGE = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 80" width="320" height="80">
  <defs>
    <linearGradient id="btnGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#ef4444"/>
      <stop offset="100%" stop-color="#dc2626"/>
    </linearGradient>
    <filter id="shadow" x="-10%" y="-10%" width="120%" height="130%">
      <feDropShadow dx="0" dy="4" stdDeviation="6" flood-color="#000000" flood-opacity="0.4"/>
    </filter>
  </defs>
  <rect x="6" y="6" width="308" height="68" rx="20" fill="#18181b" stroke="#3f3f46" stroke-width="2" filter="url(#shadow)"/>
  <rect x="18" y="14" width="170" height="52" rx="14" fill="url(#btnGrad)"/>
  <path d="M 42 28 L 42 52 L 62 40 Z" fill="#ffffff"/>
  <text x="74" y="47" font-family="system-ui, -apple-system, sans-serif" font-size="17" font-weight="800" fill="#ffffff" letter-spacing="0.5">SUBSCRIBE</text>
  <g transform="translate(245, 40)">
    <path d="M -12 -6 C -12 -12 12 -12 12 -6 C 12 4 16 8 16 10 L -16 10 C -16 8 -12 4 -12 -6 Z M -4 12 C -4 14.5 4 14.5 4 12 Z" fill="#fbbf24"/>
  </g>
</svg>
`)}`;

const SVG_LIVE_REC_BUG = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 220 64" width="220" height="64">
  <rect x="4" y="6" width="212" height="52" rx="14" fill="#09090b" fill-opacity="0.85" stroke="#ef4444" stroke-width="2"/>
  <circle cx="34" cy="32" r="9" fill="#ef4444"/>
  <circle cx="34" cy="32" r="14" fill="#ef4444" fill-opacity="0.3"/>
  <text x="56" y="39" font-family="system-ui, -apple-system, monospace" font-size="18" font-weight="900" fill="#ffffff" letter-spacing="1">LIVE REC</text>
  <rect x="164" y="22" width="36" height="20" rx="4" fill="#ef4444"/>
  <text x="170" y="37" font-family="monospace" font-size="11" font-weight="bold" fill="#ffffff">4K</text>
</svg>
`)}`;

const SVG_PODCAST_HOST_BADGE = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 280 70" width="280" height="70">
  <defs>
    <linearGradient id="podGrad" x1="0%" y1="0%" x2="100%" y2="0%">
      <stop offset="0%" stop-color="#4f46e5"/>
      <stop offset="100%" stop-color="#06b6d4"/>
    </linearGradient>
  </defs>
  <rect x="4" y="5" width="272" height="60" rx="16" fill="#0f172a" stroke="url(#podGrad)" stroke-width="2.5"/>
  <circle cx="36" cy="35" r="18" fill="url(#podGrad)"/>
  <text x="26" y="42" font-size="20">🎙️</text>
  <text x="68" y="32" font-family="sans-serif" font-size="14" font-weight="800" fill="#ffffff">PODCAST STUDIO</text>
  <text x="68" y="50" font-family="sans-serif" font-size="11" font-weight="600" fill="#38bdf8">Verified Host Stream</text>
</svg>
`)}`;

const SVG_NEON_FRAME_STAMP = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 64" width="240" height="64">
  <defs>
    <linearGradient id="neonG" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#ec4899"/>
      <stop offset="100%" stop-color="#8b5cf6"/>
    </linearGradient>
  </defs>
  <rect x="4" y="6" width="232" height="52" rx="12" fill="#18181b" stroke="url(#neonG)" stroke-width="2.5"/>
  <text x="24" y="38" font-family="system-ui, sans-serif" font-size="16" font-weight="900" fill="url(#neonG)" letter-spacing="1.5">⚡ CLIPCHAMP PRO</text>
</svg>
`)}`;

/**
 * Get built-in stock royalty-free presets (Videos, Images, Background Music, SFX)
 * Ready to be previewed and added directly to timeline tracks with 1-click!
 */
export function getStockMediaPresets(): MediaAssetItem[] {
  return [
    // 1. Stock Background Music & SFX
    {
      id: 'stock_bgm_lofi',
      name: 'Lofi Chill Beat (BGM)',
      type: 'audio',
      url: createSynthesizedAudioDataUrl('lofi'),
      durationSec: 16.0,
      thumbnailUrl: '',
      isStock: true
    },
    {
      id: 'stock_bgm_upbeat',
      name: 'Upbeat Acoustic Groove (Music)',
      type: 'audio',
      url: createSynthesizedAudioDataUrl('upbeat'),
      durationSec: 12.0,
      thumbnailUrl: '',
      isStock: true
    },
    {
      id: 'stock_sfx_chime',
      name: 'Crystal Glass Chime (SFX)',
      type: 'audio',
      url: createSynthesizedAudioDataUrl('chime'),
      durationSec: 2.5,
      thumbnailUrl: '',
      isStock: true
    },

    // 2. Stock Image Overlays & Watermarks
    {
      id: 'stock_img_subscribe',
      name: 'Subscribe & Bell Badge (Overlay)',
      type: 'image',
      url: SVG_SUBSCRIBE_BADGE,
      durationSec: 5.0,
      thumbnailUrl: SVG_SUBSCRIBE_BADGE,
      dimensions: { width: 320, height: 80 },
      isStock: true
    },
    {
      id: 'stock_img_live',
      name: 'Live Stream REC Bug (Watermark)',
      type: 'image',
      url: SVG_LIVE_REC_BUG,
      durationSec: 8.0,
      thumbnailUrl: SVG_LIVE_REC_BUG,
      dimensions: { width: 220, height: 64 },
      isStock: true
    },
    {
      id: 'stock_img_podcast_host',
      name: 'Podcast Host Lower Third (Badge)',
      type: 'image',
      url: SVG_PODCAST_HOST_BADGE,
      durationSec: 6.0,
      thumbnailUrl: SVG_PODCAST_HOST_BADGE,
      dimensions: { width: 280, height: 70 },
      isStock: true
    },
    {
      id: 'stock_img_neon_stamp',
      name: 'Clipchamp Pro Stamp (Sticker)',
      type: 'image',
      url: SVG_NEON_FRAME_STAMP,
      durationSec: 5.0,
      thumbnailUrl: SVG_NEON_FRAME_STAMP,
      dimensions: { width: 240, height: 64 },
      isStock: true
    }
  ];
}

/**
 * Probe an imported local file (Video, Image, Audio) and extract duration, dimensions, thumbnail
 */
export async function probeMediaFile(file: File): Promise<MediaAssetItem> {
  const url = URL.createObjectURL(file);
  const mime = file.type.toLowerCase();
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  const id = generateId('media');

  const isImage = mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'webp', 'svg', 'gif', 'bmp', 'ico'].includes(ext);
  const isAudio = mime.startsWith('audio/') || ['mp3', 'wav', 'm4a', 'aac', 'ogg', 'flac'].includes(ext);

  if (isImage) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          id,
          name: file.name,
          type: 'image',
          url,
          fileSize: file.size,
          durationSec: 5.0, // Default duration for image clips in video timeline
          thumbnailUrl: url,
          dimensions: { width: img.naturalWidth, height: img.naturalHeight }
        });
      };
      img.onerror = () => {
        resolve({
          id,
          name: file.name,
          type: 'image',
          url,
          fileSize: file.size,
          durationSec: 5.0,
          thumbnailUrl: url
        });
      };
      img.src = url;
    });
  }

  if (isAudio) {
    return new Promise((resolve) => {
      const audio = document.createElement('audio');
      audio.preload = 'metadata';
      audio.onloadedmetadata = () => {
        const dur = Math.max(1, isFinite(audio.duration) ? audio.duration : 10);
        resolve({
          id,
          name: file.name,
          type: 'audio',
          url,
          fileSize: file.size,
          durationSec: dur
        });
      };
      audio.onerror = () => {
        resolve({
          id,
          name: file.name,
          type: 'audio',
          url,
          fileSize: file.size,
          durationSec: 10.0
        });
      };
      audio.src = url;
    });
  }

  // Video File (default for other media or video types)
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    video.onloadedmetadata = () => {
      const dur = Math.max(1, isFinite(video.duration) ? video.duration : 15);
      video.currentTime = Math.min(1.0, dur * 0.2);
    };

    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = Math.min(320, video.videoWidth || 320);
        canvas.height = Math.min(180, video.videoHeight || 180);
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const thumb = canvas.toDataURL('image/jpeg', 0.7);
          resolve({
            id,
            name: file.name,
            type: 'video',
            url,
            fileSize: file.size,
            durationSec: Math.max(1, isFinite(video.duration) ? video.duration : 15),
            thumbnailUrl: thumb,
            dimensions: { width: video.videoWidth, height: video.videoHeight }
          });
          return;
        }
      } catch (err) {
        console.warn('Canvas video thumbnail failed:', err);
      }
      resolve({
        id,
        name: file.name,
        type: 'video',
        url,
        fileSize: file.size,
        durationSec: Math.max(1, isFinite(video.duration) ? video.duration : 15),
        dimensions: { width: video.videoWidth, height: video.videoHeight }
      });
    };

    video.onerror = () => {
      resolve({
        id,
        name: file.name,
        type: 'video',
        url,
        fileSize: file.size,
        durationSec: 15.0
      });
    };

    video.src = url;
  });
}
